import { useState } from 'react';
import { format, isPast, parseISO } from 'date-fns';
import { ko } from 'date-fns/locale';
// @mui
import {
  Card,
  Box,
  Stack,
  Typography,
  Button,
  Skeleton,
} from '@mui/material';
// hooks
import useGoals from '../../hooks/useGoals';
import useResponsive from '../../hooks/useResponsive';
// components
import SvgIconStyle from '../../components/SvgIconStyle';
import Scrollbar from '../../components/Scrollbar';
import { GoalEditDialog, GoalAddDialog } from './';

export default function GoalCard({ companyId }) {
  const smUp = useResponsive('up', 'sm');
  const { goals, totalGoals, isLoading } = useGoals(companyId);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  // 미완료 요청사항만 필터링
  const incompleteGoals = goals.filter(goal => !goal.is_completed);

  if (isLoading) {
    return (
      <Card sx={{ height: '100%', maxHeight: 200 }}>
        <Box sx={{ px: { xs: 3, sm: 5 }, py: { xs: 3, sm: 4 } }}>
          <Skeleton variant="rectangular" height={120} />
        </Box>
      </Card>
    );
  }

  // 요청사항이 하나도 없는 경우
  if (totalGoals === 0) {
    return (
      <>
        <Card sx={{ height: '100%', maxHeight: 200, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{
            px: { xs: 3, sm: 5 },
            py: { xs: 3, sm: 4 },
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
          }}>
            <Stack spacing={2} sx={{ height: '100%' }}>
              <Typography variant="h6">
                회사의 요청사항을 등록해주세요!
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                회사와 소통 후 요청내용과 일정을 등록해주세요
              </Typography>
              <Box>
                <Button
                  variant="contained"
                  onClick={() => setIsAddDialogOpen(true)}
                  size="large"
                  sx={{ px: smUp ? 4 : 0 }}
                  fullWidth={!smUp}
                  startIcon={<SvgIconStyle src={`/icons/ic_calendar.svg`} sx={{ width: 20, height: 20 }} />}
                >
                  등록하기
                </Button>
              </Box>
            </Stack>
          </Box>
        </Card>
        <GoalAddDialog
          isOpen={isAddDialogOpen}
          closeDialog={() => setIsAddDialogOpen(false)}
          companyId={companyId}
        />
      </>
    );
  }

  // 요청사항이 있는 경우
  return (
    <>
      <Card sx={{ height: '100%', maxHeight: 200, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{
          px: { xs: 3, sm: 5 },
          pt: { xs: 3, sm: 4 },
          pb: 2,
        }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              회사의 요청사항
            </Typography>
            <Button
              variant="text"
              size="small"
              sx={{ color: "text.secondary" }}
              onClick={() => setIsEditDialogOpen(true)}
              startIcon={<SvgIconStyle src={"/icons/icon-pencil-mono.svg"} sx={{ width: 16, height: 16 }} />}
              disableRipple
            >
              수정하기
            </Button>
          </Stack>
        </Box>
        {incompleteGoals.length === 0 ? (
          <Box sx={{
            px: { xs: 3, sm: 5 },
            pb: { xs: 3, sm: 4 },
          }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              진행 중인 요청사항이 없습니다.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ 
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            mb: { xs: 3, sm: 4 },
            px: 2,
          }}>
            <Scrollbar>
              <Box sx={{
                px: { xs: 1, sm: 3 }, 
              }}>
                <Stack spacing={1.5}>
                  {incompleteGoals.map((goal) => {
                    const isOverdue = goal.due_date && isPast(parseISO(goal.due_date));
                    
                    return (
                      <Stack
                        key={goal.id}
                        direction="row"
                        alignItems="flex-start"
                        spacing={1.5}
                      >
                        <SvgIconStyle
                          src="/icons/ic_calendar.svg"
                          sx={{
                            width: 20,
                            height: 20,
                            color: isOverdue ? 'error.main' : 'text.secondary',
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            flex: 1,
                            color: isOverdue ? 'error.main' : 'text.primary',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            wordBreak: 'break-word',
                          }}
                        >
                          {goal.content}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: isOverdue ? 'error.main' : 'text.secondary',
                          }}
                        >
                          {goal.due_date
                            ? format(parseISO(goal.due_date), 'MM월 dd일', { locale: ko })
                            : '기한 없음'}
                        </Typography>
                      </Stack>
                    );
                  })}
                </Stack>
              </Box>
            </Scrollbar>
          </Box>
        )}
      </Card>
      
      <GoalEditDialog
        isOpen={isEditDialogOpen}
        closeDialog={() => setIsEditDialogOpen(false)}
        companyId={companyId}
      />
    </>
  );
}